import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  offset,
  shift,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import {announce} from '@react-aria/live-announcer'
import {toast} from 'sonner'

import CopySvg from '@/assets/icons/copy.svg?react'
import myRewardsBackground from '@/assets/images/my-rewards-background-dark.png'
import {useAccountInformationValue} from '@/auth/hooks/useAccountInformation'
import type {AccountInformation} from '@/auth/model'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import HeadTags from '@/lib/head/HeadTags'
import {useCurrencySymbol} from '@/lib/locale/country'
import {useLocale} from '@/lib/locale/locale'
import {getReferrerQueryKey, useReferrer, useRewards} from '@/lib/referral/hooks/useRewards'
import {useWallet} from '@/lib/rewards/useWallet'
import {updateReferrer} from '@/services/rewardService'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {formatLocaleDateTime} from '@/utils/datetime'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'

import style from './Rewards.module.scss'

function createReferralLink(account: AccountInformation | undefined) {
  if (!account) return ''

  return `https://innosearch.ai/r/${account.refCode}`
}

async function copyText(text: string, onSuccess?: () => void, onFailure?: (e: Error) => void) {
  try {
    const clipboardItemInput = new ClipboardItem({
      'text/plain': new Blob([text], {type: 'text/plain'}),
    })
    await navigator.clipboard.write([clipboardItemInput])
    onSuccess?.()
  } catch (e) {
    onFailure?.(e as Error)
  }
}

export default memo(function Rewards() {
  const [locale] = useLocale()
  const {data: wallet} = useWallet()
  const account = useAccountInformationValue()
  const latestAccount = useLatest(account)

  const rewardAmount = wallet ? wallet.availableBalance + wallet.frozenBalance : 0

  const referralLink = createReferralLink(account)
  const latestReferralLink = useLatest(referralLink)

  const {data: rewards} = useRewards(locale)

  const currencySymbol = useCurrencySymbol()

  //----------------------------------------------------------------------------

  const [isLinkCopied, setIsLinkCopied] = useState(false)

  const copyLink = useCallback(() => {
    void copyText(
      latestReferralLink.current,
      () => {
        setIsLinkCopied(true)
        setTimeout(() => {
          setIsLinkCopied(false)
        }, 2000)
      },
      e => {
        console.error('Copy link failed', e)
      },
    )
  }, [])

  const {
    refs: linkCopyRefs,
    floatingStyles: linkCopyFloatingStyles,
    context: linkCopyContext,
  } = useFloating({
    strategy: 'absolute',
    open: isLinkCopied,
    middleware: [offset(5), flip({fallbackAxisSideDirection: 'end'}), shift()],
    whileElementsMounted: autoUpdate,
  })

  const linkCopyRole = useRole(linkCopyContext)

  const {getReferenceProps: getLinkCopyReferenceProps, getFloatingProps: getLinkCopyFloatingProps} =
    useInteractions([linkCopyRole])

  //----------------------------------------------------------------------------

  const referralMessageRef = useRef<HTMLTextAreaElement>(null)

  const [isMessageCopied, setIsMessageCopied] = useState(false)

  const copyMessage = useCallback(() => {
    if (!referralMessageRef.current) return

    const text = referralMessageRef.current.value

    void copyText(
      text,
      () => {
        setIsMessageCopied(true)

        setTimeout(() => {
          setIsMessageCopied(false)
        }, 2000)
      },
      e => {
        console.error('Copy message failed', e)
      },
    )
  }, [])

  const {
    refs: messageCopyRefs,
    floatingStyles: messageCopyFloatingStyles,
    context: messageCopyContext,
  } = useFloating({
    strategy: 'absolute',
    open: isMessageCopied,
    middleware: [offset(5), flip({fallbackAxisSideDirection: 'end'}), shift()],
    whileElementsMounted: autoUpdate,
  })

  const messageCopyRole = useRole(messageCopyContext)

  const {
    getReferenceProps: getMessageCopyReferenceProps,
    getFloatingProps: getMessageCopyFloatingProps,
  } = useInteractions([messageCopyRole])

  //----------------------------------------------------------------------------

  const {data: referrer} = useReferrer()
  const queryClient = useQueryClient()

  const [referrerInput, setReferrerInput] = useState<string>()
  const latestReferrer = useLatest(referrerInput)

  const onReferrerChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setReferrerInput(e.target.value)
  }, [])

  const handleUpdateReferrerSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (referrer) return
      if (!latestReferrer.current) return

      void updateReferrer({referrer: latestReferrer.current, account: latestAccount.current})
        .then(() => {
          toast.success('Referrer updated')
          announce('Referrer updated')

          void queryClient.invalidateQueries({
            queryKey: getReferrerQueryKey(),
          })
        })
        .catch(e => {
          toastErrorMessage(e)
        })
    },
    [referrer, queryClient],
  )

  //----------------------------------------------------------------------------
  if (!account) return null
  const mailMessage = `Hey,

I recently began shopping on Innosearch, and it's a fantastic site with great accessibility and tons of discounts. If you use the referral link below to create an account, you'll receive $10 in shopping credit: ${referralLink}

Check it out!
${account.accountName}`

  const isEmpty = !rewards || rewards.length === 0

  return (
    <>
      <HeadTags title='Rewards' />
      <div className={style.RewardsPage}>
        <h1 className={style.Heading}>Rewards</h1>
        <div className={style.ReferAndRewards}>
          <div className={style.Refer}>
            <h2>Refer a friend</h2>
            <div className={style.ReferGuideContainer}>
              <div className={style.ReferGuide}>
                Get {currencySymbol}10 for you and $10 for each friend you refer with no limit
              </div>
              <a
                href='https://blog.innosearch.ai/innosearch-ais-referral-program'
                className={style.LearnMore}
              >
                Learn more
                <VisuallyHidden>about the referral program</VisuallyHidden>
              </a>
            </div>
            <div className={style.HowContainer}>
              <h3>How?</h3>
              <div className={style.Guide}>Share this link with your friend to sign up</div>
              <div className={style.InviteLink}>
                <input
                  className={style.LinkPart}
                  aria-label='referral link'
                  value={referralLink}
                  disabled
                />
                <button
                  className={style.CopyButton}
                  aria-label='copy referral link'
                  ref={linkCopyRefs.setReference}
                  {...getLinkCopyReferenceProps({
                    'aria-expanded': undefined,
                    'aria-haspopup': undefined,
                    'aria-controls': undefined,
                  })}
                  onClick={copyLink}
                >
                  <CopySvg aria-hidden />
                  {isLinkCopied && (
                    <FloatingFocusManager context={linkCopyContext} modal={false}>
                      <div
                        className={style.Popover}
                        ref={linkCopyRefs.setFloating}
                        style={linkCopyFloatingStyles}
                        {...getLinkCopyFloatingProps()}
                      >
                        <div className={style.PopoverArrow} />
                        <div className={style.PopoverContent}>Copied!</div>
                      </div>
                    </FloatingFocusManager>
                  )}
                </button>
              </div>
              <div className={style.GuideSecondary}>Or send them the following message</div>
              <div className={style.EmailMessageContainer}>
                <VisuallyHidden>
                  <label htmlFor='message'>Referral message</label>
                </VisuallyHidden>
                <textarea
                  id='message'
                  rows={8}
                  className={style.EmailMessage}
                  ref={referralMessageRef}
                >
                  {mailMessage}
                </textarea>
                <button
                  className={style.CopyButton}
                  aria-label='copy referral message'
                  ref={messageCopyRefs.setReference}
                  {...getMessageCopyReferenceProps({
                    'aria-expanded': undefined,
                    'aria-haspopup': undefined,
                    'aria-controls': undefined,
                  })}
                  onClick={copyMessage}
                >
                  <CopySvg aria-hidden />
                  {isMessageCopied && (
                    <FloatingFocusManager context={messageCopyContext} modal={false}>
                      <div
                        className={style.Popover}
                        ref={node => {
                          messageCopyRefs.setFloating(node)
                        }}
                        style={messageCopyFloatingStyles}
                        {...getMessageCopyFloatingProps()}
                      >
                        <div className={style.PopoverArrow} />
                        <div className={style.PopoverContent}>Copied!</div>
                      </div>
                    </FloatingFocusManager>
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className={style.MyRewards}>
            <h2 className={style.MyRewardsText}>My rewards</h2>
            <div className={style.Amount}>
              {currencySymbol}
              {rewardAmount}
            </div>
            <img src={myRewardsBackground} className={style.Background} alt='' />
          </div>
        </div>
        {!referrer && (
          <div>
            <h2>Referred by a friend? Enter their code here to earn rewards</h2>
            <form className={style.Referrer} onSubmit={handleUpdateReferrerSubmit}>
              <input
                name='referrer'
                value={referrerInput}
                onChange={onReferrerChange}
                aria-disabled={!!referrer}
              />
              <PrimaryButton type='submit'>Update</PrimaryButton>
            </form>
          </div>
        )}
        <div className={style.RewardHistory}>
          <h2>Rewards history</h2>
          {rewards && rewards.length > 0 && (
            <table className={style.RewardTable}>
              <thead>
                <tr>
                  <th className={style.MessageHeader}>Information</th>
                  <th className={style.AmountHeader}>Amount change</th>
                  <th className={style.BalanceHeader}>Balance after</th>
                </tr>
              </thead>
              <tbody>
                {rewards.map(reward => (
                  <tr className={style.RewardRow} key={reward.id}>
                    <td className={style.MessageCell}>
                      <div className={style.Reason}>{reward.description}</div>
                      <div className={style.Time}>
                        <VisuallyHidden>At:</VisuallyHidden>
                        {formatLocaleDateTime(reward.updatedAt)}
                      </div>
                    </td>
                    <td className={style.AmountCell}>
                      <Price price={reward.amount} />
                    </td>
                    <td className={style.BalanceCell}>
                      <Price price={reward.balanceAfter} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {isEmpty && <div>History is empty</div>}
        </div>
      </div>
    </>
  )
})
